import { XbotCitation, XbotTextBlock } from "@dto/pyxbot-agent.dto";
import { Button, Image } from "antd";
import { FC, ReactElement, ReactNode, useCallback, useRef, useState } from "react";
import { LuArrowUpRight, LuChevronDown, LuChevronLeft, LuChevronRight, LuChevronUp } from "react-icons/lu";
import { textExport } from "../exportData";
import { XbotExportBar } from "../ExportBar";
import { XbotBlockProps } from "../host";
import { foldButtonText, foldMarkdown, MarkdownText, XbotPageNames } from "../markdownText";
import { OutLink } from "../parts";
import { TonedMarkdown, XbotBlockTone, XbotCallout } from "../tone";
import { API_URL } from "src/data/Api";
import { xbotServerPath } from "../../xbotFetch";

// A whole-block tone already has its own card; here only a mixed block's differing stretch gets one.
function Sentences({ markdown, tone, names }: { markdown: string; tone?: XbotBlockTone; names: XbotPageNames }): ReactElement {
  if (tone?.segments?.length) {
    return <>
      {tone.segments.map((segment, index) => (segment.tone === "normal"
        ? <MarkdownText key={index} markdown={segment.markdown} names={names} />
        : <XbotCallout key={index} tone={segment.tone}>
          <TonedMarkdown markdown={segment.markdown} tone={segment.tone} names={names} />
        </XbotCallout>))}
    </>;
  }
  if (tone && tone.tone !== "normal") {
    return <TonedMarkdown markdown={markdown} tone={tone.tone} names={names} />;
  }
  return <MarkdownText markdown={markdown} names={names} />;
}

// Pictures ship with the product (works with no internet); nothing is ever fetched from elsewhere.
export const MANUAL_PICTURE_ROUTE = `${API_URL.XBOT}/doc-image/`;

/** The most pictures one block shows; the answer carries at most this many. */
export const MOST_PICTURES = 8;

/** Any host would do: it only lets the browser's own rules resolve a path, and it is never asked. */
const RESOLVE_BASE = "http://page.invalid";

// Resolves the path like a browser would (dot segments, escaped dots, backslashes) so a path that only starts like the route can't escape it.
export function manualPictureAddress(address: unknown): string | null {
  if (typeof address !== "string") {
    return null;
  }
  const path = address.trim();
  if (!path.startsWith("/") || path.startsWith("//") || path.startsWith("/\\")) {
    return null;
  }
  let resolved: URL;
  try {
    resolved = new URL(path, RESOLVE_BASE);
  } catch {
    return null;
  }
  if (resolved.origin !== RESOLVE_BASE || !resolved.pathname.startsWith(MANUAL_PICTURE_ROUTE)
    || resolved.pathname.length === MANUAL_PICTURE_ROUTE.length) {
    return null;
  }
  return resolved.pathname;
}

/** The pictures of the block that are the manual's, each once, in the passage's order. */
export function manualPictures(images: unknown): string[] {
  if (!Array.isArray(images)) {
    return [];
  }
  const found: string[] = [];
  for (const image of images) {
    const address = manualPictureAddress(image);
    if (address && !found.includes(address)) {
      found.push(address);
    }
  }
  return found.slice(0, MOST_PICTURES);
}

/** Each picture's caption by its address (the manual's own words), from the block's parallel lists. */
export function pictureCaptions(images: unknown, captions: unknown): Record<string, string> {
  const found: Record<string, string> = {};
  if (!Array.isArray(images) || !Array.isArray(captions)) {
    return found;
  }
  images.forEach((image, index) => {
    const address = manualPictureAddress(image);
    const caption = captions[index];
    if (address && typeof caption === "string" && caption.trim() && !(address in found)) {
      found[address] = caption.trim();
    }
  });
  return found;
}

/** The titles of the pages cited, each once. */
function citedTitles(citations: XbotCitation[] | null | undefined): string[] {
  const titles: string[] = [];
  for (const citation of Array.isArray(citations) ? citations : []) {
    const title = typeof citation?.title === "string" ? citation.title.trim() : "";
    if (title && !titles.includes(title)) {
      titles.push(title);
    }
  }
  return titles;
}

// Names the page only when the block cites exactly one source; pictures from several pages are only counted.
function fromManual(which: string, citations: XbotCitation[] | null | undefined): string {
  const titles = citedTitles(splitCitations(Array.isArray(citations) ? citations : []).sources);
  return titles.length === 1 ? `${which} from the manual: ${titles[0]}` : `${which} from the manual`;
}

// The manual's own caption of the picture when it has one, else what fromManual says of it.
export function pictureCaption(index: number, count: number, citations: XbotCitation[] | null | undefined,
  caption?: string | null): string {
  const own = typeof caption === "string" ? caption.trim() : "";
  return own || fromManual(count > 1 ? `Picture ${index + 1} of ${count}` : "Picture", citations);
}

/** Text longer than this many characters starts folded in the bubble (whole when enlarged). */
export const TEXT_FOLD_CHARS = 480;

/** The line over the manual's thumbnails: "3 pictures from the manual: Alerting". */
export function picturesLine(count: number, citations: XbotCitation[] | null | undefined): string {
  return fromManual(count === 1 ? "Picture" : `${count} pictures`, citations);
}

const isWebAddress = (url: string | null | undefined) => /^https?:\/\//.test((url || "").trim());

/** The pages the text is quoted from, and apart from them the addresses the quoted text itself links to. */
export function splitCitations(citations: XbotCitation[]): { sources: XbotCitation[]; linked: XbotCitation[] } {
  return {
    sources: citations.filter((citation) => citation.kind !== "link"),
    linked: citations.filter((citation) => citation.kind === "link"),
  };
}

// The cited pages as one quiet line, a link with its arrow each.
const CitationLine: FC<{ citations: XbotCitation[]; label: string; group: string }> = ({ citations, label, group }) => <div className="xm-xbot-sources" role="group" aria-label={group}>
  <span className="xm-xbot-muted">{label}:</span>
  {citations.map((citation, index) => <span key={index} className="xm-xbot-source">
    <OutLink url={citation.url}>{citation.title}</OutLink>
    {isWebAddress(citation.url) ? <LuArrowUpRight aria-hidden="true" className="xm-xbot-value-arrow" /> : null}
  </span>)}
</div>;

const Sources: FC<{ citations: XbotCitation[] }> = ({ citations }) => {
  const { sources, linked } = splitCitations(citations);
  return <>
    {sources.length ? <CitationLine citations={sources} label={sources.length === 1 ? "Source" : "Sources"} group="Sources" /> : null}
    {linked.length ? <CitationLine citations={linked} label="Links in the text" group="Links in the text" /> : null}
  </>;
};

const THUMB_HEIGHT = 72;

// A picture shown large with an arrow at each of its edges, to the one before and the one after.
const PictureWithArrows: FC<{ picture: ReactNode; at: number; total: number; page: (offset: number) => void }> = ({ picture, at, total, page }) => {
  if (total < 2) {
    return <>{picture}</>;
  }
  return <div className="xm-xbot-preview-frame">
    {picture}
    <button type="button" className="xm-xbot-preview-arrow is-prev" aria-label="Previous picture" disabled={at <= 0}
      onClick={(event) => { event.stopPropagation(); page(-1); }}><LuChevronLeft aria-hidden="true" /></button>
    <button type="button" className="xm-xbot-preview-arrow is-next" aria-label="Next picture" disabled={at >= total - 1}
      onClick={(event) => { event.stopPropagation(); page(1); }}><LuChevronRight aria-hidden="true" /></button>
  </div>;
};

// The manual's pictures as small pictures in a row, a click shows them large in the page.
const Pictures: FC<{ pictures: string[]; citations: XbotCitation[]; captions: Record<string, string> }> = ({ pictures, citations, captions }) => {
  const [failed, setFailed] = useState<string[]>([]);
  const shown = pictures.filter((address) => !failed.includes(address));
  const line = picturesLine(pictures.length, citations);
  // the group is told its pictures outright: it then always pages through them ("2 / 7") from whichever is clicked,
  // rather than relying on each thumbnail registering itself with it
  // the same address the thumbnail loads: a click finds its picture in the group by it
  const items = shown.map((address, index) => ({
    src: xbotServerPath(address), alt: pictureCaption(index, pictures.length, citations, captions[address]),
  }));
  // the preview's own paging (its toolbar hands it over), for arrows drawn at the picture's edges: antd puts its own
  // at the edges of the screen, where on a wide one nobody looks
  const paging = useRef<((offset: number) => void) | null>(null);
  const preview = {
    actionsRender: (toolbar: ReactElement, info: { actions: { onActive: (offset: number) => void } }) => {
      paging.current = info.actions.onActive;
      return toolbar;
    },
    imageRender: (picture: ReactElement, info: { current?: number }) => <PictureWithArrows
      picture={picture} at={info.current ?? 0} total={items.length} page={(offset) => paging.current?.(offset)} />,
  };
  return <figure className="xm-xbot-thumbs">
    {/* the manual's pictures are often see-through: the large view gets a white ground of its own (xbot.less) */}
    <Image.PreviewGroup items={items} preview={preview} classNames={{ popup: { root: "xm-xbot-picture-preview xm-xbot-preview" } }}>
      {shown.map((address, index) => <Image
        key={address}
        src={xbotServerPath(address)}
        alt={pictureCaption(index, pictures.length, citations, captions[address])}
        title={captions[address] || undefined}
        height={THUMB_HEIGHT}
        // an answer may carry eight of them, and a long thread many answers: each is fetched when it comes into view
        loading="lazy"
        decoding="async"
        onError={() => setFailed((was) => (was.includes(address) ? was : [...was, address]))}
      />)}
    </Image.PreviewGroup>
    <figcaption className="xm-xbot-muted">
      {failed.length ? `${line} (${failed.length === pictures.length ? "could not be loaded" : `${failed.length} could not be loaded`})` : line}
    </figcaption>
  </figure>;
};

export const TextBlock: FC<XbotBlockProps<XbotTextBlock>> = ({ block, host, tone }) => {
  const config = block.config;
  const markdown = config.markdown || "";
  // a long text is cut after a whole paragraph or step; a text mixing tones stays whole
  const fold = !host.wide && !tone?.segments?.length && markdown.length > TEXT_FOLD_CHARS
    ? foldMarkdown(markdown, TEXT_FOLD_CHARS)
    : null;
  const [unfolded, setUnfolded] = useState(false);
  const citations = Array.isArray(config.citations) ? config.citations.filter((citation) => citation?.title) : [];
  const pictures = manualPictures(config.images);
  const captions = pictureCaptions(config.images, config.image_captions);
  const collect = useCallback(() => Promise.resolve(textExport(block)), [block]);
  // the names the sentences say open their object's page, the way a table's names do
  const names: XbotPageNames = { links: config.links, onOpen: host.onOpenLink };
  // in the bubble Copy sits in the answer's corner (XbotFramedAnswer), not on a line of its own
  const copy = host.copyText !== false && host.wide === true;
  const folded = fold !== null && !unfolded;
  return <div className={folded ? "xm-xbot-text is-folded" : "xm-xbot-text"}>
    <Sentences markdown={folded ? fold.shown : markdown} tone={tone} names={names} />
    {fold
      // a button like the table's "Show all rows", its arrow saying which way the text goes
      ? <Button
        size="small"
        className="xm-xbot-text-unfold"
        icon={unfolded ? <LuChevronUp aria-hidden /> : <LuChevronDown aria-hidden />}
        aria-expanded={unfolded}
        onClick={() => setUnfolded(!unfolded)}
      >
        {unfolded ? "Show less" : foldButtonText(fold)}
      </Button>
      : null}
    {pictures.length ? <Pictures pictures={pictures} citations={citations} captions={captions} /> : null}
    {citations.length ? <Sources citations={citations} /> : null}
    {copy ? <XbotExportBar collect={collect} name={block.title || "answer"} copyOnly /> : null}
  </div>;
};
